import React, { useState, useEffect, useCallback } from "react";
import { Plus, KeyRound, Trash2, Edit3, Check, X as XIcon, AtSign, Star, Ban, RotateCcw } from "lucide-react";
import { Modal } from "./Modal";
import { Badge } from "./Badges";
import { fmtDate } from "../lib/format";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";

const ROLES = [
  { value: "customer_admin", label: "Customer Admin" },
  { value: "customer_viewer", label: "Viewer" },
];

/** Modal listing all customer-portal logins ("contacts") for a single customer.
 *  Lets PTE staff add / edit role / disable / reset-password / remove contacts. */
export function CustomerContactsModal({ customer, canEdit, canDelete, onClose, onChanged }) {
  const [rows, setRows] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "customer_viewer" });
  const [editing, setEditing] = useState(null); // {id, name, email, role}
  const [tempPw, setTempPw] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null); // row pending hard-delete
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/users/customers", { params: { customer_id: customer.id, limit: 200 } });
      setRows(r.data.items || []);
    } catch (e) { toast.error(apiErr(e)); setRows([]); }
  }, [customer.id]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.name || !form.email) return toast.error("Name and email are required");
    try {
      const r = await api.post("/users/customers", { ...form, customer_id: customer.id });
      if (r.data.temp_password) setTempPw({ email: r.data.email, pw: r.data.temp_password });
      toast.success("Contact added");
      setAdding(false); setForm({ name: "", email: "", role: "customer_viewer" });
      load(); onChanged && onChanged();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/users/customers/${editing.id}`, {
        name: editing.name, email: editing.email, role: editing.role,
      });
      toast.success("Contact updated");
      setEditing(null); load(); onChanged && onChanged();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const toggleActive = async (row) => {
    try {
      await api.patch(`/users/customers/${row.id}`, { active: !row.active });
      toast.success(row.active ? "Contact disabled" : "Contact re-activated");
      load(); onChanged && onChanged();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const removeContact = async () => {
    if (!confirmDel) return;
    setDeleting(true);
    try {
      await api.delete(`/users/customers/${confirmDel.id}`);
      toast.success(`Removed ${confirmDel.name || confirmDel.email || "contact"}`);
      setConfirmDel(null); load(); onChanged && onChanged();
    } catch (e) { toast.error(apiErr(e)); } finally { setDeleting(false); }
  };

  const resetPw = async (row) => {
    try {
      const r = await api.post(`/users/customers/${row.id}/reset-password`);
      setTempPw({ email: row.email, pw: r.data.temp_password });
      toast.success("Password reset");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const setPrimary = async (row) => {
    try {
      await api.patch(`/users/customers/${row.id}`, { is_primary: true });
      toast.success(`${row.name || row.email} is now the primary contact`);
      load(); onChanged && onChanged();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Modal open onClose={onClose} icon={AtSign} title="Contacts & portal users"
      subtitle={customer.customer_name}
      footer={<button className="btn gold" onClick={onClose}>Done</button>}>
      <div className="tbl-wrap" style={{ marginBottom: canEdit ? 14 : 0 }}>
        <table>
          <thead><tr><th>Name</th><th>Email</th><th>Access</th><th>Status</th><th>Last login</th>{canEdit && <th></th>}</tr></thead>
          <tbody>
            {rows === null && <tr><td colSpan={canEdit ? 6 : 5} className="t-mut">Loading…</td></tr>}
            {rows && rows.length === 0 && <tr><td colSpan={canEdit ? 6 : 5} className="t-mut">No contacts yet — add the first one below.</td></tr>}
            {rows && rows.map((u) => {
              const isEd = editing && editing.id === u.id;
              return (
                <tr key={u.id} data-testid={`contact-row-${u.id}`}>
                  <td>{isEd ? <input className="input" style={{ padding: "4px 8px" }} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /> : (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                      <span className="t-strong">{u.name || "—"}</span>
                      {u.is_primary && <Badge label="Primary" cls="b-gold" />}
                    </span>
                  )}</td>
                  <td className="mono">{isEd ? <input className="input" style={{ padding: "4px 8px" }} value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} /> : <span className="t-mut">{u.email}</span>}</td>
                  <td>{isEd ? (
                    <select className="input" style={{ padding: "4px 8px" }} value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })}>
                      {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  ) : <Badge label={ROLES.find((r) => r.value === u.role)?.label || u.role} cls={u.role === "customer_admin" ? "b-gold" : "b-slate"} />}</td>
                  <td>{u.active === false ? <Badge label="Disabled" cls="b-red" /> : u.must_reset ? <Badge label="Reset pending" cls="b-amber" /> : <Badge label="Active" cls="b-green" />}</td>
                  <td className="mono t-mut" style={{ fontSize: 12 }}>{fmtDate(u.last_login_at)}</td>
                  {canEdit && (
                    <td style={{ textAlign: "right" }}>
                      {isEd ? (
                        <span style={{ display: "inline-flex", gap: 4 }}>
                          <button className="icon-btn" style={{ width: 26, height: 26 }} onClick={saveEdit} data-testid={`contact-save-${u.id}`}><Check size={14} /></button>
                          <button className="icon-btn" style={{ width: 26, height: 26 }} onClick={() => setEditing(null)}><XIcon size={14} /></button>
                        </span>
                      ) : (
                        <span style={{ display: "inline-flex", gap: 4 }}>
                          <button className="icon-btn" title={u.is_primary ? "Primary contact" : "Make primary contact"} style={{ width: 26, height: 26, color: u.is_primary ? "var(--gold)" : "var(--text-3)" }} onClick={() => !u.is_primary && setPrimary(u)} data-testid={`contact-primary-${u.id}`} disabled={u.is_primary}>
                            <Star size={13} fill={u.is_primary ? "currentColor" : "none"} />
                          </button>
                          <button className="icon-btn" title="Edit" style={{ width: 26, height: 26 }} onClick={() => setEditing({ id: u.id, name: u.name || "", email: u.email, role: u.role })} data-testid={`contact-edit-${u.id}`}><Edit3 size={13} /></button>
                          <button className="icon-btn" title="Reset password" style={{ width: 26, height: 26 }} onClick={() => resetPw(u)} data-testid={`contact-reset-${u.id}`}><KeyRound size={13} /></button>
                          <button className="icon-btn" title={u.active === false ? "Re-activate" : "Disable (keep record)"} style={{ width: 26, height: 26, color: u.active === false ? "var(--green)" : "var(--text-3)" }} onClick={() => toggleActive(u)} data-testid={`contact-toggle-${u.id}`}>{u.active === false ? <RotateCcw size={13} /> : <Ban size={13} />}</button>
                          {canDelete && (
                            <button className="icon-btn" title="Delete contact permanently" style={{ width: 26, height: 26, color: "var(--red)" }} onClick={() => setConfirmDel(u)} data-testid={`contact-delete-${u.id}`}><Trash2 size={13} /></button>
                          )}
                        </span>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {canEdit && !adding && (
        <button className="btn ghost" style={{ width: "100%", justifyContent: "center", borderStyle: "dashed" }} onClick={() => setAdding(true)} data-testid="add-contact-btn">
          <Plus size={15} /> Add contact / portal user
        </button>
      )}
      {canEdit && adding && (
        <div className="card pad" style={{ background: "var(--bg-soft, #fafafa)", marginTop: 4 }}>
          <div className="form-grid">
            <div className="fld"><label>Full name <span className="req">*</span></label><input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" /></div>
            <div className="fld"><label>Email <span className="req">*</span></label><input className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" /></div>
            <div className="fld"><label>Access level</label>
              <select className="input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 10 }}>
            <button className="btn ghost" onClick={() => { setAdding(false); setForm({ name: "", email: "", role: "customer_viewer" }); }}>Cancel</button>
            <button className="btn gold" onClick={add} data-testid="save-contact-btn">Create</button>
          </div>
        </div>
      )}

      {confirmDel && (
        <Modal open onClose={() => !deleting && setConfirmDel(null)} icon={Trash2} title="Delete contact?"
          subtitle={confirmDel.name || confirmDel.email}
          footer={<>
            <button className="btn ghost" onClick={() => setConfirmDel(null)} disabled={deleting} data-testid="contact-delete-cancel">Cancel</button>
            <button className="btn" style={{ background: "var(--red)", color: "#fff" }} onClick={removeContact} disabled={deleting} data-testid="contact-delete-confirm">{deleting ? "Deleting…" : "Delete permanently"}</button>
          </>}>
          <p style={{ fontSize: 13, marginBottom: 8 }}>
            This permanently removes <b>{confirmDel.name || confirmDel.email}</b> from <b>{customer.customer_name}</b>’s contact list{confirmDel.email ? <> (<span className="mono">{confirmDel.email}</span>)</> : null}.
          </p>
          <p style={{ fontSize: 12.5, color: "var(--text-2)", margin: 0 }}>
            Only this contact / portal-login record is deleted. The customer, equipment, certificates, jobs and calibration history are <b>not</b> affected. This action is logged in the audit trail.
          </p>
        </Modal>
      )}

      {tempPw && (
        <Modal open onClose={() => setTempPw(null)} title="Temporary password generated"
          subtitle={tempPw.email}
          footer={<button className="btn gold" onClick={() => setTempPw(null)}>Done</button>}>
          <p style={{ fontSize: 13, marginBottom: 10 }}>Share this password with the contact — they will be prompted to set their own on first sign-in.</p>
          <div className="mono" style={{ background: "var(--bg-soft, #fafafa)", padding: "12px 14px", borderRadius: 8, fontSize: 16, fontWeight: 700, letterSpacing: 1, textAlign: "center" }}>{tempPw.pw}</div>
        </Modal>
      )}
    </Modal>
  );
}
